import axios from 'axios';
import { getAuthHeader } from '../auth/utils/authInterceptors';
import { AUTH_CONSTANTS } from '../auth/constants/authConstants';

/**
 * 微信相关服务
 * 处理与微信API相关的请求
 */
class WechatService {
  /**
   * 获取微信JS-SDK签名
   * @param {string} url 当前页面URL
   * @returns {Promise<Object>} 签名信息
   */
  async getJssdkSignature(url) {
    try {
      const token = localStorage.getItem(AUTH_CONSTANTS.STORAGE_KEYS.TOKEN);
      if (!token) {
        console.error('未找到认证token');
        return null;
      }

      const response = await axios.get(AUTH_CONSTANTS.API_PATHS.GET_SIGNATURE, {
        params: { 
          url: encodeURIComponent(url)
        },
        headers: {
          ...getAuthHeader(token).headers,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.data.code === 200) {
        return response.data.data;
      } else {
        console.error('获取微信签名失败:', response.data.message);
        return null;
      }
    } catch (error) {
      console.error('获取微信签名请求失败:', error);
      return null;
    }
  }
  
  /**
   * 配置微信JS-SDK
   * @param {Object} wx 微信JS-SDK对象
   * @param {string} url 当前页面URL
   * @param {Array<string>} jsApiList 需要使用的JS接口列表
   * @returns {Promise<boolean>} 配置是否成功
   */
  async configJssdk(wx, url, jsApiList = ['updateAppMessageShareData', 'updateTimelineShareData']) {
    try {
      const signatureInfo = await this.getJssdkSignature(url);
      
      if (!signatureInfo) {
        return false;
      }
      
      return new Promise((resolve) => {
        wx.config({
          debug: process.env.NODE_ENV === 'development',
          appId: signatureInfo.appId,
          timestamp: signatureInfo.timestamp,
          nonceStr: signatureInfo.nonceStr,
          signature: signatureInfo.signature,
          jsApiList
        });
        
        wx.ready(() => {
          console.log('微信JS-SDK配置成功');
          resolve(true);
        });
        
        wx.error((res) => {
          console.error('微信JS-SDK配置失败:', res);
          resolve(false);
        });
      });
    } catch (error) {
      console.error('配置微信JS-SDK失败:', error);
      return false;
    }
  }

  /**
   * 获取地址信息
   * @param {number} latitude 纬度
   * @param {number} longitude 经度
   * @returns {Promise<Object>} 地址信息
   */
  async getAddress(latitude, longitude) {
    try {
      const token = localStorage.getItem(AUTH_CONSTANTS.STORAGE_KEYS.TOKEN);
      if (!token) {
        console.error('未找到认证token');
        return null;
      }

      const response = await axios.get('/newapi/location/address', {
        params: {
          latitude,
          longitude
        },
        headers: {
          ...getAuthHeader(token).headers,
          'Content-Type': 'application/json'
        }
      });

      if (response.data.code === 200) {
        return response.data.data;
      } else {
        console.error('获取地址信息失败:', response.data.message);
        return null;
      }
    } catch (error) {
      console.error('请求地址信息服务失败:', error);
      return null;
    }
  }

  /**
   * 获取当前天气信息
   * @param {number} latitude 纬度
   * @param {number} longitude 经度
   * @returns {Promise<Object>} 天气信息
   */
  async getCurrentWeather(latitude, longitude) {
    try {
      const token = localStorage.getItem(AUTH_CONSTANTS.STORAGE_KEYS.TOKEN);
      if (!token) {
        console.error('未找到认证token');
        return null;
      }

      const response = await axios.get('/newapi/weather/current', {
        params: {
          latitude,
          longitude
        },
        headers: {
          ...getAuthHeader(token).headers,
          'Content-Type': 'application/json'
        }
      });

      if (response.data.code === 200) {
        return response.data.data;
      } else {
        console.error('获取天气信息失败:', response.data.message);
        return null;
      }
    } catch (error) {
      console.error('请求天气信息服务失败:', error);
      return null;
    }
  }
}

export const wechatService = new WechatService(); 